import axios from "axios";
import { useEffect, useRef, useState } from "react";
import {
  FiBriefcase,
  FiCheckCircle,
  FiClipboard,
  FiCode,
  FiDollarSign,
  FiList,
  FiMapPin,
  FiX,
  FiArrowLeft,
  FiSend,
  FiZap,
  FiSearch,
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import "../index.css";
import "./EmployeeDashboard.css";
import "./EmployeeLeaves.css";

const API_BASE_URL = 'https://ingrainhirebackend.ingrainsystems.com/api';

function PostNewJob() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [formData, setFormData] = useState({
    role: searchParams.get("role") || "",
    description: "",
    skills: "",
    experience: "",
    location: "",
    salary: "",
    department: searchParams.get("department") || "",
    vacancies: 1,
    assessmentIds: [],
    expiryDate: "",
    companyName: "",
    aboutCompany: "",
  });

  const [quizzes, setQuizzes] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingQuizzes, setFetchingQuizzes] = useState(false);
  const [fetchingEmployees, setFetchingEmployees] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [assessmentSearch, setAssessmentSearch] = useState("");
  const [showRoleDropdown, setShowRoleDropdown] = useState(false);
  const [showDeptDropdown, setShowDeptDropdown] = useState(false);
  const [showExpDropdown, setShowExpDropdown] = useState(false);
  const [editingCompany, setEditingCompany] = useState(false);
  const roleRef = useRef(null);
  const deptRef = useRef(null);
  const expRef = useRef(null);

  // Get clientId from localStorage
  const getClientId = () => {
    return localStorage.getItem('clientId') || localStorage.getItem('clientCustomId') || '';
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roleRef.current && !roleRef.current.contains(event.target)) {
        setShowRoleDropdown(false);
      }
      if (deptRef.current && !deptRef.current.contains(event.target)) {
        setShowDeptDropdown(false);
      }
      if (expRef.current && !expRef.current.contains(event.target)) {
        setShowExpDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchQuizzes();
    fetchRoles();
    fetchDepartments();
    fetchEmployees();
    fetchCompanyDefaults();
    fetchExperiences();
  }, []);

  const fetchExperiences = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/experience-options/all`);
      if (res.data.success) {
        setExperiences(res.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch experiences:", err);
    }
  };

  const fetchCompanyDefaults = async () => {
    try {
      const clientId = getClientId();
      const res = await axios.get(`${API_BASE_URL}/admin/settings?clientId=${clientId}`);
      if (res.data.success && res.data.settings) {
        const { companyName, aboutCompany } = res.data.settings;
        setFormData(prev => ({
          ...prev,
          companyName: companyName || "Timely Health",
          aboutCompany: aboutCompany || "",
        }));
      }
    } catch (err) {
      console.error("Failed to fetch company defaults:", err);
    }
  };

  const fetchQuizzes = async () => {
    setFetchingQuizzes(true);
    try {
      const clientId = getClientId();
      let url;
      if (clientId) {
        url = `${API_BASE_URL}/admin/getallquizes/${clientId}`;
      } else {
        url = `${API_BASE_URL}/admin/getallquizes`;
      }
      const res = await axios.get(url).catch(() => null);
      if (res && res.data && res.data.quizzes) {
        setQuizzes(res.data.quizzes);
      }
    } catch (err) {
      console.error("Failed to fetch quizzes:", err);
    } finally {
      setFetchingQuizzes(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const clientId = getClientId();
      let url;
      if (clientId) {
        url = `${API_BASE_URL}/roles/all?clientId=${clientId}`;
      } else {
        url = `${API_BASE_URL}/roles/all`;
      }
      const res = await axios.get(url);
      if (res.data.success || Array.isArray(res.data) || res.data.data) {
        let rolesData = [];
        if (Array.isArray(res.data)) rolesData = res.data;
        else if (Array.isArray(res.data.data)) rolesData = res.data.data;
        else if (Array.isArray(res.data.roles)) rolesData = res.data.roles;
        
        const uniqueRoles = rolesData.map((r, index) => {
            if (typeof r === 'string') return { _id: index, name: r };
            return { _id: r._id || index, name: r.name || r.title || r.role || "Unknown Role" };
        }).filter(r => r.name !== "Unknown Role");
        
        setRoles(uniqueRoles);
      }
    } catch (err) {
      console.error("Failed to fetch roles:", err);
    }
  };
  
  const fetchDepartments = async () => {
    try {
      const clientId = getClientId();
      let url;
      if (clientId) {
        url = `${API_BASE_URL}/department/all?clientId=${clientId}`;
      } else {
        url = `${API_BASE_URL}/department/all`;
      }
      const res = await axios.get(url);
      if (res.data.success || Array.isArray(res.data) || res.data.data) {
        let deptData = [];
        if (Array.isArray(res.data)) deptData = res.data;
        else if (Array.isArray(res.data.data)) deptData = res.data.data;
        else if (Array.isArray(res.data.departments)) deptData = res.data.departments;
        
        const uniqueDepts = deptData.map((d, index) => {
            if (typeof d === 'string') return { _id: index, name: d };
            return { _id: d._id || index, name: d.name || d.departmentName || "Unknown Dept" };
        }).filter(d => d.name !== "Unknown Dept");
        
        setDepartments(uniqueDepts);
      }
    } catch (err) {
      console.error("Failed to fetch departments:", err);
    }
  };

  const fetchEmployees = async () => {
    setFetchingEmployees(true);
    try {
      const clientId = getClientId();
      let url;
      if (clientId) {
        url = `${API_BASE_URL}/employees/get-employees/${clientId}`;
      } else {
        url = `${API_BASE_URL}/employees/get-employees`;
      }
      const res = await axios.get(url);
      const empData = Array.isArray(res.data) ? res.data : res.data.employees || res.data.data || [];
      setEmployees(empData);
    } catch (err) {
      console.error("Failed to fetch employees:", err);
    } finally {
      setFetchingEmployees(false);
    }
  };

  // Reflect correct department based on role from employees list
  useEffect(() => {
    if (formData.role && (formData.department === "General" || !formData.department) && employees.length > 0) {
      const roleToMatch = formData.role.toLowerCase().trim();
      const matchedEmp = employees.find(emp => {
        const empRole = (emp.role || emp.position || emp.designation || "").toLowerCase().trim();
        return empRole.includes(roleToMatch) || roleToMatch.includes(empRole);
      });

      if (matchedEmp) {
        const matchedDept = (matchedEmp.department || matchedEmp.dept || "").trim();
        if (matchedDept && matchedDept.toLowerCase() !== "general") {
          setFormData(prev => ({ ...prev, department: matchedDept }));
        }
      }
    }
  }, [formData.role, employees]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAssessmentToggle = (assessmentId) => {
    setFormData((prev) => {
      const current = prev.assessmentIds;
      return {
        ...prev,
        assessmentIds: current.includes(assessmentId)
          ? current.filter((id) => id !== assessmentId)
          : [...current, assessmentId],
      };
    });
  };

  const handleSubmit = async (e, status = "active") => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    const clientId = getClientId();
    if (!clientId) {
      setMessage({ type: "error", text: "Client not authenticated. Please login again." });
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/jobs/create`, { 
        ...formData, 
        status,
        clientId 
      });
      if (response.data.success) {
        setMessage({ type: "success", text: status === "draft" ? "Job saved as draft!" : "Job posted successfully!" });
        setTimeout(() => navigate("/job-post"), 1800);
      }
    } catch (error) {
      const errorMsg =
        error.response?.data?.message || error.message || "Failed to create job post.";
      setMessage({ type: "error", text: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewRole = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/roles/create`, {
        name: formData.role,
        description: "Custom role created from Job Post"
      });
      if (res.data.success) {
        setRoles(prev => [...prev, res.data.data]);
        setShowRoleDropdown(false);
        setMessage({ type: "success", text: "New role added successfully!" });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Failed to add new role" });
    }
  };

  const handleAddNewExperience = async () => {
    try {
      const res = await axios.post(`${API_BASE_URL}/experience-options/create`, {
        label: formData.experience
      });
      if (res.data.success) {
        setExperiences(prev => [...prev, res.data.data]);
        setShowExpDropdown(false);
        setMessage({ type: "success", text: "New experience added successfully!" });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Failed to add new experience" });
    }
  };

  const sortedQuizzes = [...quizzes].sort((a, b) => {
    const roleA = (a.role || a.category || "").toLowerCase();
    const roleB = (b.role || b.category || "").toLowerCase();
    const target = formData.role.toLowerCase();
    if (target) {
      if (roleA === target && roleB !== target) return -1;
      if (roleB === target && roleA !== target) return 1;
    }
    return 0;
  });

  const filteredQuizzes = sortedQuizzes.filter(quiz => 
    (quiz.topic || quiz.title || "").toLowerCase().includes(assessmentSearch.toLowerCase()) ||
    (quiz.role || quiz.category || "").toLowerCase().includes(assessmentSearch.toLowerCase())
  );

  return (
    <div style={{backgroundColor:'white'}} className="emp-dash ">
      <main className="p-4 sm:p-6 lg:p-8">
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              Post <span>New Job</span>
            </h1>
            <p className="emp-dash__subtitle">
              Create a new job posting for recruitment.
            </p>
          </div>
          <div className="emp-dash__date-pill">
            <FiBriefcase />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>
      {/* Status Message */}
      {message.text && (
        <div
          className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
            message.type === "success"
              ? "bg-emerald-50 text-emerald-700 border border-emerald-100"
              : "bg-rose-50 text-rose-600 border border-rose-100"
          }`}
        >
          {message.type === "success" ? (
            <FiCheckCircle size={18} />
          ) : (
            <FiX size={18} />
          )}
          <span className="text-sm font-bold">{message.text}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Left Column – Core Details */}
          <div className="lg:col-span-2 space-y-6">

            {/* Basic Info Card */}
            <div className="emp-dash__card p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                  <FiBriefcase size={15} className="text-indigo-600" />
                </div>
                <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Job Details
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {/* Job Role */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest">
                    Job Position <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative group" ref={roleRef}>
                    <FiBriefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors z-10" size={15} />
                    <input
                      type="text"
                      name="role"
                      value={formData.role}
                      onChange={(e) => {
                        handleChange(e);
                        setShowRoleDropdown(true);
                      }}
                      onFocus={() => setShowRoleDropdown(true)}
                      required
                      autoComplete="off"
                      className="w-full pl-11 pr-10 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800 bg-white"
                      placeholder="Enter or search position..."
                    />
                    {showRoleDropdown && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto no-scrollbar">
                        {roles.filter(r => r.name.toLowerCase().includes(formData.role.toLowerCase())).length === 0 ? (
                          <div className="p-3 text-xs text-gray-500 flex flex-col items-center">
                            <span>No existing roles match</span>
                            {formData.role && (
                              <button 
                                type="button" 
                                onClick={handleAddNewRole}
                                className="mt-2 text-indigo-600 font-semibold hover:underline"
                              >
                                + Add "{formData.role}" as new role
                              </button>
                            )}
                          </div>
                        ) : (
                          roles.filter(r => r.name.toLowerCase().includes(formData.role.toLowerCase())).map((role) => (
                            <div
                              key={role._id}
                              className="px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer"
                              onClick={() => {
                                setFormData((prev) => ({ ...prev, role: role.name }));
                                setShowRoleDropdown(false);
                              }}
                            >
                              {role.name}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-300">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Salary */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest">
                    Salary / Package
                  </label>
                  <div className="relative group">
                    <FaRupeeSign className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors" size={15} />
                    <input
                      type="text"
                      name="salary"
                      value={formData.salary}
                      onChange={handleChange}
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800"
                      placeholder="5 - 12 LPA"
                    />
                  </div>
                </div>

                {/* Experience */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest">
                    Experience Required <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative group" ref={expRef}>
                    <FiList className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors z-10" size={15} />
                    <input
                      type="text"
                      name="experience"
                      value={formData.experience}
                      onChange={(e) => {
                        handleChange(e);
                        setShowExpDropdown(true);
                      }}
                      onFocus={() => setShowExpDropdown(true)}
                      required
                      autoComplete="off"
                      className="w-full pl-11 pr-10 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800 bg-white"
                      placeholder="Enter or select experience..."
                    />
                    {showExpDropdown && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto no-scrollbar">
                        {experiences.filter(e => e.label.toLowerCase().includes(formData.experience.toLowerCase())).length === 0 ? (
                          <div className="p-3 text-xs text-gray-500 flex flex-col items-center">
                            <span>No existing experience match</span>
                            {formData.experience && (
                              <button 
                                type="button" 
                                onClick={handleAddNewExperience}
                                className="mt-2 text-indigo-600 font-semibold hover:underline"
                              >
                                + Add "{formData.experience}" as new experience
                              </button>
                            )}
                          </div>
                        ) : (
                          experiences.filter(e => e.label.toLowerCase().includes(formData.experience.toLowerCase())).map((exp) => (
                            <div
                              key={exp._id}
                              className="px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer"
                              onClick={() => {
                                setFormData((prev) => ({ ...prev, experience: exp.label }));
                                setShowExpDropdown(false);
                              }}
                            >
                              {exp.label}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-gray-300">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Location */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest">
                    Location <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative group">
                    <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors" size={15} />
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      required
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800"
                      placeholder="Hyderabad / Remote"
                    />
                  </div>
                </div>

                {/* Department */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest">
                    Department <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative group" ref={deptRef}>
                    <FiList className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors z-10" size={15} />
                    <input
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={(e) => {
                        handleChange(e);
                        setShowDeptDropdown(true);
                      }}
                      onFocus={() => setShowDeptDropdown(true)}
                      required
                      autoComplete="off"
                      className="w-full pl-11 pr-10 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800 bg-white"
                      placeholder="Enter or search department..."
                    />
                    {showDeptDropdown && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto no-scrollbar">
                        {departments.filter(d => d.name.toLowerCase().includes(formData.department.toLowerCase())).length === 0 ? (
                          <div className="p-3 text-xs text-gray-500 text-center">No existing departments match</div>
                        ) : (
                          departments.filter(d => d.name.toLowerCase().includes(formData.department.toLowerCase())).map((dept) => (
                            <div
                              key={dept._id}
                              className="px-4 py-2.5 text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 cursor-pointer"
                              onClick={() => {
                                setFormData((prev) => ({ ...prev, department: dept.name }));
                                setShowDeptDropdown(false);
                              }}
                            >
                              {dept.name}
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Vacancies */}
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest">
                    Vacancies <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative group">
                    <FiZap className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors" size={15} />
                    <input
                      type="number"
                      name="vacancies"
                      value={formData.vacancies}
                      onChange={handleChange}
                      required
                      min="1"
                      className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800"
                      placeholder="e.g. 3"
                    />
                  </div>
                </div>
              </div>

              {/* Skills */}
              <div className="space-y-1.5 mt-5">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest">
                  Required Skills <span className="text-rose-500">*</span>
                </label>
                <div className="relative group">
                  <FiCode className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors" size={15} />
                  <input
                    type="text"
                    name="skills"
                    value={formData.skills}
                    onChange={handleChange}
                    required
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800"
                    placeholder="Communication Skills, Positive Attitude, Ready to learn"
                  />
                </div>
                <p className="text-[10px] text-gray-400">Separate multiple skills with commas</p>
              </div>
            </div>

            {/* Job Description Card */}
            <div className="emp-dash__card p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                  <FiList size={15} className="text-blue-600" />
                </div>
                <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Job Description
                </h2>
              </div>
              <div className="relative group">
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={7}
                  className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-sm text-gray-800 resize-none"
                  placeholder="Describe the role responsibilities, key objectives, team context, and growth opportunities..."
                />
              </div>
            </div>

            {/* Application Deadline Card */}
            <div className="emp-dash__card p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                  <FiClipboard size={15} className="text-amber-600" />
                </div>
                <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Application Deadline
                </h2>
              </div>
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest">
                  Expiry Date
                </label>
                <div className="relative group">
                  <FiMapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors" size={15} />
                  <input
                    type="date"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleChange}
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-4 focus:ring-amber-600/5 focus:border-amber-600 transition-all outline-none text-sm text-gray-800"
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-2 italic">Applications will be disabled after this date.</p>
              </div>
            </div>

            {/* Company Info Card */}
            <div className="emp-dash__card p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                    <FiBriefcase size={15} className="text-violet-600" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">Company Info</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setEditingCompany(prev => !prev)}
                  className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-lg border transition-all ${
                    editingCompany
                      ? "bg-violet-600 text-white border-violet-600"
                      : "bg-violet-50 text-violet-600 border-violet-100 hover:bg-violet-100"
                  }`}
                >
                  {editingCompany ? <FiCheckCircle size={12} /> : <FiZap size={12} />}
                  {editingCompany ? "Done" : "Edit"}
                </button>
              </div>

              {editingCompany ? (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest">Company Name</label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-violet-600 focus:ring-4 focus:ring-violet-600/5 transition-all outline-none text-sm font-medium"
                      placeholder="e.g. Timely Health"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest">About Company</label>
                    <textarea
                      name="aboutCompany"
                      value={formData.aboutCompany}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3.5 rounded-xl border border-gray-200 focus:border-violet-600 focus:ring-4 focus:ring-violet-600/5 transition-all outline-none text-sm font-medium resize-none"
                      placeholder="Brief description of your company, mission, and culture..."
                    />
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const clientId = getClientId();
                        await axios.put(`${API_BASE_URL}/admin/settings/company`, {
                          clientId,
                          companyName: formData.companyName,
                          aboutCompany: formData.aboutCompany,
                        });
                        setEditingCompany(false);
                      } catch (err) {
                        console.error("Failed to save company defaults", err);
                      }
                    }}
                    className="w-full py-3 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold uppercase tracking-widest transition-all shadow-lg shadow-violet-600/20"
                  >
                    Save as Default
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-violet-50/60 rounded-xl border border-violet-100">
                    <p className="text-[9px] font-bold text-violet-400 uppercase tracking-widest mb-1">Company Name</p>
                    <p className="text-sm font-bold text-gray-800">{formData.companyName || "Timely Health"}</p>
                  </div>
                  {formData.aboutCompany && (
                    <div className="p-3 bg-gray-50 rounded-xl border border-gray-100">
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mb-1">About</p>
                      <p className="text-xs text-gray-600 leading-relaxed">{formData.aboutCompany}</p>
                    </div>
                  )}
                  <p className="text-[9px] text-gray-400 italic">Click Edit to customise for this job post or update defaults.</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Column – Assessments + Actions */}
          <div className="space-y-6">

            {/* Assessments Card */}
            <div className="emp-dash__card p-6">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <FiClipboard size={15} className="text-emerald-600" />
                </div>
                <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Assessments
                </h2>
              </div>
              <p className="text-[10px] text-gray-400 mb-3 ml-10">
                Link multiple assessments to this job
              </p>

              <div className="relative mb-4">
                <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-600 transition-colors z-10" size={14} />
                <input
                  type="text"
                  placeholder="Search assessments..."
                  value={assessmentSearch}
                  onChange={(e) => setAssessmentSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-gray-50/50 rounded-xl border border-gray-200 focus:bg-white focus:ring-2 focus:ring-indigo-600/20 focus:border-indigo-600 transition-all outline-none text-[11px] text-gray-800"
                />
              </div>

              {fetchingQuizzes ? (
                <div className="flex items-center gap-2 py-4 justify-center">
                  <div className="w-4 h-4 border-2 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
                  <span className="text-xs text-gray-400">Syncing banks...</span>
                </div>
              ) : quizzes.length === 0 ? (
                <div className="text-center py-8">
                  <FiClipboard className="mx-auto text-3xl text-gray-200 mb-2" />
                  <p className="text-[10px] text-gray-400 uppercase tracking-[0.2em]">
                    No Assessments Available
                  </p>
                  <p className="text-[9px] text-gray-400 mt-1">
                    Create assessments in the manager first.
                  </p>
                </div>
              ) : filteredQuizzes.length === 0 ? (
                <div className="text-center py-6 border-2 border-dashed border-gray-100 rounded-xl">
                  <p className="text-[10px] text-gray-400 uppercase tracking-widest">
                    No results found
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto no-scrollbar">
                  {filteredQuizzes.map((quiz) => {
                    const isSelected = formData.assessmentIds.includes(quiz._id);
                    const isSuggested =
                      formData.role &&
                      (quiz.role || quiz.category || "").toLowerCase() ===
                        formData.role.toLowerCase();
                    return (
                      <label
                        key={quiz._id}
                        htmlFor={`quiz-${quiz._id}`}
                        className={`flex items-center gap-3 p-2.5 rounded-xl border-2 cursor-pointer transition-all ${
                          isSelected
                            ? "bg-indigo-50 border-indigo-400 text-indigo-700"
                            : "bg-gray-50 border-transparent hover:border-gray-200"
                        }`}
                      >
                        <input
                          id={`quiz-${quiz._id}`}
                          type="checkbox"
                          className="hidden"
                          checked={isSelected}
                          onChange={() => handleAssessmentToggle(quiz._id)}
                        />
                        <div
                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                            isSelected
                              ? "bg-indigo-600 border-indigo-600"
                              : "bg-white border-gray-300"
                          }`}
                        >
                          {isSelected && (
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                        <div className="flex flex-col flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <span className="text-xs font-medium truncate">{quiz.topic || quiz.title}</span>
                            {isSuggested && (
                              <span className="bg-emerald-100 text-emerald-700 text-[8px] px-1.5 py-0.5 rounded-full uppercase tracking-tighter flex-shrink-0">
                                Match
                              </span>
                            )}
                          </div>
                          <span className="text-[9px] text-gray-800 uppercase tracking-wider mt-0.5">
                            {quiz.role || quiz.category || "General"} · {quiz.experienceLevel || "All"}
                          </span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}

              {formData.assessmentIds.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
                  <span className="text-xs text-indigo-600 font-bold">
                    {formData.assessmentIds.length} assessment{formData.assessmentIds.length > 1 ? "s" : ""} linked
                  </span>
                </div>
              )}
            </div>

            {/* Action Buttons Card */}
            <div className="emp-dash__card p-6 space-y-3">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-lg shadow-indigo-600/20"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <FiSend size={15} />
                    Post Job Opening
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={(e) => handleSubmit(e, "draft")}
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl border border-indigo-600 text-indigo-600 text-sm font-bold hover:bg-indigo-50 transition-all disabled:opacity-60 shadow-sm"
              >
                {loading ? "Saving..." : "Save as Draft"}
              </button>

              <button
                type="button"
                onClick={() => navigate(-1)}
                className="w-full flex items-center justify-center gap-2 py-3 px-6 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-all"
              >
                <FiX size={15} />
                Cancel
              </button>

              {/* Quick Tip */}
              <div className="mt-2 p-3 bg-amber-50 rounded-xl border border-amber-100 flex items-start gap-2">
                <FiZap size={13} className="text-amber-500 mt-0.5 flex-shrink-0" />
                <p className="text-[10px] text-amber-700">
                  Linking assessments will auto-filter candidates based on their quiz performance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </form>
      </main>
    </div>
  );
}

export default PostNewJob;